import React from 'react'
import { Paperclip } from 'lucide-react'
import { StandardColumn } from '../../../../components/StandardPage'
import { OverflowCell } from '../../../../components/OverflowCell'
import { Product, Script } from '../AddProduct/types'
import { formatCurrency } from '../ViewProduct/utils'
import { StyledSelect } from './style'
import ItemsDisplay from './ItemsDisplay'

export const productFinalScreenKey = 'product_final'

export const getSelectedScript = (
  product: Product,
  selectedScriptIds: Record<number, number>,
): Script | null => {
  const selectedId = product.id ? selectedScriptIds[product.id] : undefined
  return (
    product.scripts?.find((script) => script.id === selectedId) ||
    product.scripts?.[0] ||
    null
  )
}

export const getScriptDeadlineInDays = (script: Script | null): number => {
  if (!script) return 0
  return (script.additionalDeadline || 0) + (script.totalDeliveryTime || 0)
}

const formatItemsText = (script: Script | null): string => {
  if (!script?.items || script.items.length === 0) return 'Nenhum item'
  return script.items
    .map((item) => `${item.order}. ${item.name || 'Item sem nome'}`)
    .join(', ')
}

const renderAttachmentsCell = (product: Product) => {
  const files = (product as any).attachmentFiles || (product as any).Attachments || (product as any).attachments || []
  if (!Array.isArray(files) || files.length === 0) return '-'
  return (
    <OverflowCell
      text={files.map((f: any) => f.originalName || f.fileName || f.GeneralFile?.originalName || f.GeneralFile?.fileName || 'Anexo').join(', ')}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <Paperclip size={14} color="#0090d3" />
        {files.length} {files.length === 1 ? 'anexo' : 'anexos'}
      </span>
    </OverflowCell>
  )
}

export const buildProductFinalColumns = (
  selectedScriptIds: Record<number, number>,
  onScriptChange: (productId: number, scriptId: number) => void,
): StandardColumn<Product>[] => [
  {
    id: 'name',
    label: 'Produto',
    accessor: (product) => product.name || '-',
    cell: (product) => <OverflowCell text={product.name || '-'} maxWidth={200} />,
  },
  {
    id: 'code',
    label: 'Código',
    accessor: (product) => (product as any).productCode || (product as any).code || '-',
    cell: (product) => <OverflowCell text={(product as any).productCode || (product as any).code || '-'} maxWidth={150} />,
    exportValue: (product) => (product as any).productCode || (product as any).code || '',
  },
  {
    id: 'quantity',
    label: 'Quantidade',
    accessor: (product) => product.quantity != null ? String(product.quantity) : '-',
    cell: (product) => <>{product.quantity != null ? String(product.quantity) : '-'}</>,
    exportValue: (product) => product.quantity != null ? String(product.quantity) : '',
  },
  {
    id: 'norm',
    label: 'Norma',
    accessor: (product) => product.norm || '-',
    cell: (product) => <OverflowCell text={product.norm || '-'} maxWidth={250} />,
  },
  {
    id: 'ncm',
    label: 'NCM',
    accessor: (product) => (product as any).Ncm?.ncm || (product as any).ncm || '-',
    cell: (product) => <OverflowCell text={(product as any).Ncm?.ncm || (product as any).ncm || '-'} maxWidth={150} />,
    exportValue: (product) => (product as any).Ncm?.ncm || (product as any).ncm || '',
  },
  {
    id: 'format',
    label: 'Formato',
    accessor: (product) => (product as any).formatName || (product as any).Format?.type || (product as any).format || '-',
    cell: (product) => <OverflowCell text={(product as any).formatName || (product as any).Format?.type || (product as any).format || '-'} maxWidth={150} />,
    exportValue: (product) => (product as any).formatName || (product as any).Format?.type || (product as any).format || '',
  },
  {
    id: 'unit',
    label: 'Unidade',
    accessor: (product) => (product as any).unitName || (product as any).UnitOfMeasurement?.name || (product as any).unit || '-',
    cell: (product) => <OverflowCell text={(product as any).unitName || (product as any).UnitOfMeasurement?.name || (product as any).unit || '-'} maxWidth={100} />,
    exportValue: (product) => (product as any).unitName || (product as any).UnitOfMeasurement?.name || (product as any).unit || '',
  },
  {
    id: 'attachments',
    label: 'Anexos',
    sortable: false,
    defaultHidden: true,
    disableBulkEdit: true,
    accessor: (product) => {
      const files = (product as any).attachmentFiles || (product as any).Attachments || (product as any).attachments || []
      return Array.isArray(files) ? files.length : 0
    },
    cell: (product) => renderAttachmentsCell(product),
    exportValue: (product) => {
      const files = (product as any).attachmentFiles || (product as any).Attachments || (product as any).attachments || []
      return Array.isArray(files) ? files.map((f: any) => f.originalName || f.fileName || f.GeneralFile?.originalName || f.GeneralFile?.fileName || 'Anexo').join(', ') : ''
    },
  },
  {
    id: 'script',
    label: 'Script',
    sortable: false,
    disableBulkEdit: true,
    accessor: (product) => getSelectedScript(product, selectedScriptIds)?.name || 'Nenhum',
    cell: (product) => {
      const selectedScript = getSelectedScript(product, selectedScriptIds)
      if (product.scripts && product.scripts.length > 1) {
        return (
          <StyledSelect
            className="custom-select"
            value={selectedScript?.id || ''}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
              if (product.id) onScriptChange(product.id, Number(event.target.value))
            }}
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
          >
            {product.scripts.map((script) => (
              <option key={script.id} value={script.id}>
                {script.name}
              </option>
            ))}
          </StyledSelect>
        )
      }
      return selectedScript?.name || 'Nenhum'
    },
  },
  {
    id: 'items',
    label: 'Itens',
    sortable: false,
    disableBulkEdit: true,
    accessor: (product) => formatItemsText(getSelectedScript(product, selectedScriptIds)),
    cell: (product) => {
      const items = getSelectedScript(product, selectedScriptIds)?.items
      if (!items || items.length === 0) return 'Nenhum item'
      return <ItemsDisplay items={items} maxWidth={200} />
    },
  },
  {
    id: 'deadline',
    label: 'Prazo (dias)',
    accessor: (product) => getScriptDeadlineInDays(getSelectedScript(product, selectedScriptIds)),
  },
  {
    id: 'cost',
    label: 'Custo (últ.)',
    disableBulkEdit: true,
    accessor: (product) => getSelectedScript(product, selectedScriptIds)?.cost || 0,
    cell: (product) => formatCurrency(getSelectedScript(product, selectedScriptIds)?.cost || 0),
    exportValue: (product) => getSelectedScript(product, selectedScriptIds)?.cost || 0,
  },
  {
    id: 'price',
    label: 'Preço (últ)',
    disableBulkEdit: true,
    accessor: (product) => (getSelectedScript(product, selectedScriptIds)?.cost || 0) * 1.3,
    cell: (product) => formatCurrency((getSelectedScript(product, selectedScriptIds)?.cost || 0) * 1.3),
    exportValue: (product) => (getSelectedScript(product, selectedScriptIds)?.cost || 0) * 1.3,
  },
]
